/** Ruído de gradiente 1D em [−1, 1] (contínuo, sem alocação): o tremor não "pisca" como seno puro. */
export function noise1(t: number, seed: number): number {
  const i = Math.floor(t);
  const f = t - i;
  const g0 = gradient(i, seed) * f;
  const g1 = gradient(i + 1, seed) * (f - 1);
  const u = f * f * (3 - 2 * f);
  return Math.max(-1, Math.min(1, 2 * (g0 + (g1 - g0) * u)));
}

function gradient(i: number, seed: number): number {
  let h = Math.imul(i, 374761393) ^ Math.imul(seed, 668265263);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return ((h & 0xffff) / 0x8000) - 1;
}

/** Mola amortecida de um eixo (o mergulho do pouso, o soco do acerto), em passos fixos de 1/240 s. */
export class Kick {
  value = 0;
  velocity = 0;
  private readonly omega: number;
  private readonly zeta: number;

  constructor(omega: number, zeta: number) {
    this.omega = omega;
    this.zeta = zeta;
  }

  impulse(velocity: number): void {
    this.velocity += velocity;
  }

  step(dt: number): number {
    // Longe demais (aba de trás, travada): a mola já teria parado.
    if (dt > 0.25) {
      this.value = 0;
      this.velocity = 0;
      return 0;
    }
    const h = 1 / 240;
    for (let t = dt; t > 1e-9; t -= h) {
      const s = Math.min(h, t);
      this.velocity += (-this.omega * this.omega * this.value - 2 * this.zeta * this.omega * this.velocity) * s;
      this.value += this.velocity * s;
    }
    return this.value;
  }
}

/** Afinação do impacto (F55, `docs/game/camera.md`). */
export const SHAKE = {
  /** Trauma perdido por segundo. */
  decay: 1.5,
  /** Frequência do ruído (Hz). */
  frequency: 18,
  /** Amplitude com trauma 1: translação (m) e giro (rad). */
  x: 0.16,
  y: 0.12,
  z: 0.08,
  pitch: 0.025,
  yaw: 0.025,
  roll: 0.05,
  /** Pouso: a partir desta velocidade de queda (m/s), mergulho por m/s acima dela, teto e trauma. */
  landFrom: 9,
  landPerSpeed: 0.25,
  landMax: 4,
  landTraumaFrom: 16,
  /** Soco do próprio acerto: velocidade da mola (m/s, para a frente) e trauma. */
  punch: 1.1,
  punchTrauma: 0.18,
  /** "Reduzir lampejos" corta o tremor à metade. */
  reduced: 0.5,
} as const;

/** Desvio deste quadro: translação (m, no espaço da câmera) e giro (rad). */
export interface ShakeOffset {
  x: number;
  y: number;
  z: number;
  pitch: number;
  yaw: number;
  roll: number;
}

const PENDING = 8;
/** Um impulso marcado que venceu há mais que isto (ms: aba escondida, quadro travado) sai sem tremer. */
export const SHAKE_STALE_MS = 250;

/**
 * Impacto da câmera (doc 02 §19, F55): trauma que decai e vira ruído em translação e giro (o
 * deslocamento é proporcional ao quadrado do trauma: golpe fraco quase não treme), mais duas molas
 * (o mergulho do pouso e o soco do próprio acerto) e impulsos marcados para o instante do impacto (o
 * golpe do chefe, a Pedra Fulminada). Tudo escala pela opção "Tremor da câmera" (`scale`).
 */
export class CameraShake {
  private trauma = 0;
  private time = 0;
  /** Intensidade das Opções (0 a 1; 0 desliga) já com "Reduzir lampejos". */
  scale = 1;
  private readonly dip = new Kick(16, 0.55);
  private readonly punch = new Kick(16, 0.55);
  /** Impulsos marcados: instante (ms) e trauma; NaN = vaga livre. */
  private readonly pendingAt = new Float64Array(PENDING).fill(Number.NaN);
  private readonly pendingAmount = new Float64Array(PENDING);
  readonly offset: ShakeOffset = { x: 0, y: 0, z: 0, pitch: 0, yaw: 0, roll: 0 };

  /** Intensidade de um golpe: clamp(2,5 × dano / (Vida + Patuá máximos), 0,15, 1). */
  static intensity(damage: number, poolMax: number): number {
    return Math.max(0.15, Math.min(1, (2.5 * damage) / Math.max(1, poolMax)));
  }

  /** Trauma de um impacto a `distance` m, caindo até zero em `radius`. */
  static falloff(amount: number, distance: number, radius: number): number {
    const k = 1 - distance / radius;
    return k <= 0 ? 0 : amount * k * k;
  }

  add(intensity: number): void {
    if (this.scale > 0 && intensity > 0) this.trauma = Math.min(1, this.trauma + intensity);
  }

  /** Trauma para o instante `at` (ms): o golpe pesado treme quando cai, não quando é anunciado. */
  schedule(at: number, intensity: number): void {
    if (intensity <= 0) return;
    let slot = 0;
    for (let i = 0; i < PENDING; i++) {
      if (Number.isNaN(this.pendingAt[i] as number)) {
        slot = i;
        break;
      }
      if ((this.pendingAt[i] as number) < (this.pendingAt[slot] as number)) slot = i;
    }
    this.pendingAt[slot] = at;
    this.pendingAmount[slot] = intensity;
  }

  /** Pouso a `speed` m/s de queda: mergulho de mola e, numa queda grande, trauma. */
  land(speed: number): void {
    if (speed <= SHAKE.landFrom) return;
    this.dip.impulse(-Math.min(SHAKE.landMax, (speed - SHAKE.landFrom) * SHAKE.landPerSpeed));
    if (speed > SHAKE.landTraumaFrom) this.add(Math.min(0.6, (speed - SHAKE.landTraumaFrom) * 0.04));
  }

  /** O próprio golpe acertou: soco curto para a frente. */
  hit(): void {
    this.punch.impulse(SHAKE.punch);
    this.add(SHAKE.punchTrauma);
  }

  get level(): number {
    return this.trauma;
  }

  /** Desvio deste quadro (em `offset`, reaproveitado). */
  step(dt: number, now: number): ShakeOffset {
    for (let i = 0; i < PENDING; i++) {
      const at = this.pendingAt[i] as number;
      if (!Number.isNaN(at) && now >= at) {
        if (now - at <= SHAKE_STALE_MS) this.add(this.pendingAmount[i] as number);
        this.pendingAt[i] = Number.NaN;
      }
    }
    this.time += dt;
    this.trauma = Math.max(0, this.trauma - SHAKE.decay * dt);
    const dip = this.dip.step(dt) * this.scale;
    const punch = this.punch.step(dt) * this.scale;
    const o = this.offset;
    const k = this.trauma * this.trauma * this.scale;
    const t = this.time * SHAKE.frequency;
    // `|| 0`: parado, zero de verdade (sem −0).
    o.x = k * SHAKE.x * noise1(t, 1) || 0;
    o.y = k * SHAKE.y * noise1(t, 2) + dip || 0;
    o.z = k * SHAKE.z * noise1(t, 3) - punch || 0;
    o.pitch = k * SHAKE.pitch * noise1(t, 4) + dip * 0.15 - punch * 0.04 || 0;
    o.yaw = k * SHAKE.yaw * noise1(t, 5) || 0;
    o.roll = k * SHAKE.roll * noise1(t, 6) || 0;
    return o;
  }
}
